import * as React from 'react';
import { ArrowRight, Check, ChevronRight, GitBranch, Info, Plus, Send, Zap } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  Callout,
  DetailHead,
  ListGroupLabel,
  RecordRow,
  WorkspaceButton,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import type { AreaCoverage } from '@/lib/api/discovery';
import { useAreaFindingsQuery, useWithdrawEvidence } from '@/lib/api/discovery-findings';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { CoverageStatusChip } from '../coverage-bits';
import { useDiscoveryNavigate } from '../discovery-layout';
import { nextStepText, plural } from '../discovery-logic';
import { DocumentEvidenceForm } from './document-evidence-form';
import { SaidRow, SourceChip } from './evidence-bits';
import { areaStatusLine, mapStateText, withdrawnToast } from './findings-logic';
import { FollowUpForm } from './follow-up-form';
import { PainPointsSection } from './pain-points';

/**
 * One area, read top to bottom (T-0188, E12-F07): where it stands and
 * what to do next, the pain points, what people said, what is still
 * waiting for review, and its process map. Prototype: `domainBody` in
 * `docs/discovery/prototype/discovery.js` (`#findings/D5`).
 */

export function AreaFindingsView({
  engagementId,
  areaId,
  areas,
  highlight,
  nothingYet,
}: {
  engagementId: string;
  areaId: string;
  areas: ReadonlyArray<AreaCoverage>;
  /** An evidence id to bring into view, from the ledger. */
  highlight: string | null;
  /** Nothing confirmed or waiting anywhere in the engagement yet. */
  nothingYet: boolean;
}) {
  // After "Look again", ask for a minute while AI reads (Decision 54.23).
  const [polling, setPolling] = React.useState(false);
  React.useEffect(() => {
    if (!polling) return;
    const t = setTimeout(() => setPolling(false), 60_000);
    return () => clearTimeout(t);
  }, [polling]);
  const q = useAreaFindingsQuery(engagementId, areaId, polling);
  const go = useDiscoveryNavigate(engagementId);
  const { toast } = useToast();
  const withdraw = useWithdrawEvidence(engagementId);
  const [source, setSource] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<'follow-up' | 'evidence' | null>(null);
  const [showDone, setShowDone] = React.useState(false);
  const [withdrawing, setWithdrawing] = React.useState<string | null>(null);
  const [withdrawError, setWithdrawError] = React.useState<string | null>(null);

  // Opened from the ledger: bring that item into view once.
  const scrolled = React.useRef(false);
  React.useEffect(() => {
    if (!highlight || !q.data || scrolled.current) return;
    scrolled.current = true;
    document.getElementById(`evidence-${highlight}`)?.scrollIntoView?.({ block: 'center' });
  }, [highlight, q.data]);

  if (q.error) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        <ErrorMessage error={q.error} />
        <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="flex items-center justify-center gap-2 p-8 text-body-sm text-ink-500">
        <Spinner /> Loading the area…
      </div>
    );
  }

  const f = q.data;
  const names = new Map(f.sources.map((s) => [s.key, s.name]));
  const next = nextStepText(f.next_step, names);
  const step = f.next_step;
  const openInterview =
    (step.kind === 'review' || step.kind === 'scheduled_interview') && step.interview_id
      ? { id: step.interview_id, code: step.interview_code ?? 'the interview' }
      : null;
  const covered = f.coverage.status === 'corroborated';
  const selected = source && f.sources.some((s) => s.key === source) ? source : null;
  const said = f.said.filter((e) => !selected || e.source_key === selected);
  const confirmed = f.said.filter((e) => e.status === 'confirmed');
  const waitingFrom = f.waiting.interviews;
  const waitCodes = waitingFrom.map((i) => i.code);
  // Documents the evidence finder read (T-0257); an older API sends none.
  const waitDocs = f.waiting.documents ?? [];
  const waitFrom = [...waitCodes, ...waitDocs.map((d) => d.title)];

  const doWithdraw = (evidenceId: string, number: string | null, reason: string) => {
    setWithdrawError(null);
    withdraw.mutate(
      { id: evidenceId, reason, key: newIdempotencyKey() },
      {
        onSuccess: (r) => {
          setWithdrawing(null);
          toast({ message: withdrawnToast(number, r) });
        },
        onError: (e) =>
          setWithdrawError(e instanceof ApiError ? e.message : 'Could not withdraw it.'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3.5">
      {nothingYet ? (
        <Callout
          tone="tip"
          icon={Info}
          actions={
            <WorkspaceButton onClick={() => go({ to: 'sessions' })}>
              Go to interviews
            </WorkspaceButton>
          }
        >
          Findings fill in as you confirm evidence from interviews, questionnaires and documents.
          Nothing is here yet.
        </Callout>
      ) : null}

      <DetailHead
        title={f.area.label}
        meta={f.area.name}
        trailing={
          <CoverageStatusChip status={f.coverage.status} label={areaStatusLine(f.coverage)} />
        }
      />

      <section
        aria-label="Where this area stands"
        className="-mt-1 flex flex-col gap-3 rounded-card border border-ink-100 bg-paper-0 px-[18px] py-4"
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500">
            Sources {f.coverage.sources} of {f.coverage.required}
          </span>
          {f.sources.length ? (
            f.sources.map((s) => (
              <SourceChip
                key={s.key}
                source={s}
                on={selected === s.key}
                onToggle={() => setSource(selected === s.key ? null : s.key)}
              />
            ))
          ) : (
            <span className="text-meta text-ink-500">None yet</span>
          )}
        </div>
        <Callout
          tone={next.done ? 'ok' : 'ask'}
          icon={next.done ? Check : ArrowRight}
          actions={
            openInterview ? (
              <WorkspaceButton
                variant="primary"
                onClick={() => go({ to: 'sessions', interview: openInterview.id })}
              >
                Open {openInterview.code}
              </WorkspaceButton>
            ) : undefined
          }
        >
          {next.text}
        </Callout>
        <div className="flex flex-wrap gap-4">
          {!covered ? (
            <button type="button" className={linkishClass()} onClick={() => setForm('follow-up')}>
              <Send className="h-3 w-3" aria-hidden="true" /> Ask someone a follow-up
            </button>
          ) : null}
          <button type="button" className={linkishClass()} onClick={() => setForm('evidence')}>
            <Plus className="h-3 w-3" aria-hidden="true" /> Add evidence from a document
          </button>
          {f.area.definition ? (
            <button
              type="button"
              className={linkishClass()}
              aria-expanded={showDone}
              onClick={() => setShowDone((v) => !v)}
            >
              <Info className="h-3 w-3" aria-hidden="true" />{' '}
              {showDone ? 'Hide' : 'What “done” looks like'}
            </button>
          ) : null}
        </div>
        {showDone && f.area.definition ? (
          <p className="rounded-card bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700">
            {f.area.definition}
          </p>
        ) : null}
      </section>

      {form === 'follow-up' ? (
        <FollowUpForm
          engagementId={engagementId}
          areaId={areaId}
          areaLabel={f.area.label}
          onDone={() => setForm(null)}
        />
      ) : null}
      {form === 'evidence' ? (
        <DocumentEvidenceForm
          engagementId={engagementId}
          areaId={areaId}
          areas={areas}
          onDone={() => setForm(null)}
        />
      ) : null}

      <PainPointsSection
        engagementId={engagementId}
        areaId={areaId}
        areaLabel={f.area.label}
        painPoints={f.pain_points}
        confirmed={confirmed}
        onLookAgainStarted={() => setPolling(true)}
      />

      <section aria-label="What people said">
        <ListGroupLabel
          count={said.length}
          action={
            selected ? (
              <span className="text-meta font-normal text-ink-500">
                from {names.get(selected)} ·{' '}
                <button type="button" className={linkishClass()} onClick={() => setSource(null)}>
                  show everyone
                </button>
              </span>
            ) : undefined
          }
        >
          What people said
        </ListGroupLabel>
        {said.length ? (
          <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
            {said.map((e) => (
              <SaidRow
                key={e.evidence_id}
                item={e}
                highlighted={highlight === e.evidence_id}
                withdrawing={withdrawing === e.evidence_id}
                busy={withdraw.isPending}
                error={withdrawing === e.evidence_id ? withdrawError : null}
                onStartWithdraw={() => {
                  setWithdrawError(null);
                  setWithdrawing(e.evidence_id);
                }}
                onCancelWithdraw={() => setWithdrawing(null)}
                onWithdraw={(reason) => doWithdraw(e.evidence_id, e.number, reason)}
              />
            ))}
          </div>
        ) : (
          <p className="text-meta text-ink-500">Nothing confirmed yet.</p>
        )}
        {f.waiting.count > 0 ? (
          <Callout
            tone="ai"
            icon={Zap}
            className="mt-2.5"
            actions={
              // One review queue across interviews and documents, filtered to this area.
              <WorkspaceButton onClick={() => go({ to: 'sessions', review: `area:${areaId}` })}>
                Review {f.waiting.count === 1 ? 'it' : `all ${f.waiting.count}`}
              </WorkspaceButton>
            }
          >
            {plural(f.waiting.count, 'more item')}
            {waitFrom.length ? ` from ${waitFrom.join(' and ')}` : ''}{' '}
            {f.waiting.count === 1 ? 'is' : 'are'} waiting for review. They count once you confirm
            them.
          </Callout>
        ) : null}
      </section>

      {f.processes.length ? (
        <div>
          {f.processes.map((p) => (
            <RecordRow
              key={p.process_id}
              icon={<GitBranch className="h-3.5 w-3.5" />}
              onClick={() => go({ to: 'maps' })}
            >
              <span className="flex-1">Process map: {p.name}</span>
              <span className="text-meta text-ink-500">{mapStateText(p.map_state)}</span>
              <ChevronRight className="h-3.5 w-3.5 flex-none text-ink-300" aria-hidden="true" />
            </RecordRow>
          ))}
        </div>
      ) : null}
    </div>
  );
}
